import classnames from 'classnames';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useNavigate, useOutlet } from 'react-router';
import styled from 'styled-components';

import { resetStack } from '#src/atoms/layout/ContentWithStickyHeader/index.tsx';
import { FullWidthContainer } from '#src/atoms/layout/FullWidthContainer/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useIsDesktopAndAbove } from '#src/hooks/device/useIsDesktopAndAbove.ts';
import { desktopAndAbove } from '#src/style/mediaQuery.ts';

export const ListWithDetailLayoutWrapper = styled.div`
  --area-height: calc(
    var(--100vh) - var(--current-stack-height) - var(--extra-stack-height, 0px)
  );
  @media ${desktopAndAbove} {
    min-height: var(--area-height);
    &.list {
      display: grid;
      grid-template-columns: 424px 1fr;
      flex: 1;
      & > *:first-child {
        border-right: 1px solid var(--c-nd-100);
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-gutter: stable;
      }
    }
  }
  & > *.reverse:first-child {
    display: flex;
    flex-direction: column-reverse;
  }
`;

const MainContent = styled(FullWidthContainer)`
  &.noPadding {
    padding: 0;
  }
  > *:only-child {
    min-height: 100%;
  }
  @media ${desktopAndAbove} {
    &.list {
      position: sticky;
      top: var(--current-stack-height);
      max-height: var(--area-height);
      > * {
        ${resetStack}
      }
    }
  }
`;

const RightPanel = styled(FullWidthContainer)`
  padding-bottom: var(--triple-unit);
  min-width: 0;
  &.noPadding {
    padding: 0;
  }
  > *:only-child {
    height: 100%;
  }
  @media ${desktopAndAbove} {
    padding-bottom: 0;
  }
`;

const DialogWrapper = styled(FullWidthContainer.NoPadding)`
  height: 100%;
`;

type Props = {
  children?: ReactNode;
  outletFallback?: ReactNode;
  outletContext?: unknown;
  loading?: boolean;
  noPadding?: boolean;
  reverse?: boolean;
  /**
   * Combine with <LineupsLayoutSwitch /> to navigate back
   * to the list path when switching from list to grid
   */
  scrollRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  forceNoMobileDialog?: boolean;
  noRightPanelPadding?: boolean;
  extraStackHeight?: number;
};

export const ListWithDetailLayout = ({
  children,
  outletFallback,
  outletContext,
  loading,
  noPadding,
  noRightPanelPadding,
  reverse,
  scrollRef,
  className,
  forceNoMobileDialog,
  extraStackHeight = 0,
}: Props) => {
  const outlet = useOutlet(outletContext);
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const navigate = useNavigate();

  const isList = isDesktopAndAbove || forceNoMobileDialog;

  return (
    <ListWithDetailLayoutWrapper
      className={classnames(className, { list: isList })}
      style={
        { '--extra-stack-height': `${extraStackHeight}px` } as CSSProperties
      }
    >
      <MainContent
        className={classnames({ noPadding, reverse, list: isList })}
        ref={scrollRef}
      >
        {children}
      </MainContent>
      {!isList && !loading && (
        <Dialog
          open={!!outlet}
          onClose={
            isDesktopAndAbove
              ? () => {
                  navigate('..', { replace: true });
                }
              : undefined
          }
          fullScreen={!isDesktopAndAbove}
        >
          <DialogWrapper>{outlet}</DialogWrapper>
        </Dialog>
      )}

      {!!isList && (
        <RightPanel className={classnames({ noPadding: noRightPanelPadding })}>
          {loading ? null : outlet || outletFallback}
        </RightPanel>
      )}
    </ListWithDetailLayoutWrapper>
  );
};
